import React, { createContext, useContext, useState, useEffect } from 'react';
import SeasonService from '../services/apiSeasonService';
import { safeConsole } from '../utils/productionUtils';

// Create the season context
const SeasonContext = createContext();

/**
 * Season Provider Component
 * Manages current season state across the application
 */
export const SeasonProvider = ({ children }) => {
  const [currentSeason, setCurrentSeason] = useState(null);
  const [realCurrentSeason, setRealCurrentSeason] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to validate season format client-side for immediate feedback
  const isValidSeasonFormat = (season) => {
    if (!season || typeof season !== 'string') return false;
    const seasonRegex = /^\d{4}-\d{2}$/;
    return seasonRegex.test(season);
  };

  // Initialize season data from backend
  useEffect(() => {
    const initializeSeasons = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get current season and available seasons from backend
        const [currentSeasonData, availableSeasonsData] = await Promise.all([
          SeasonService.getCurrentSeason(),
          SeasonService.getAvailableSeasons()
        ]);

        // Check if there's a saved season preference
        const savedSeason = localStorage.getItem('selectedSeason');
        
        if (savedSeason && isValidSeasonFormat(savedSeason)) {
          // Validate saved season exists in available seasons
          if (availableSeasonsData.available_seasons.includes(savedSeason)) {
            setCurrentSeason(savedSeason);
          } else {
            // Saved season is invalid, use current season
            setCurrentSeason(currentSeasonData.current_season);
            localStorage.setItem('selectedSeason', currentSeasonData.current_season);
          }
        } else {
          // No saved season, use current season
          setCurrentSeason(currentSeasonData.current_season);
          localStorage.setItem('selectedSeason', currentSeasonData.current_season);
        }

        setAvailableSeasons(availableSeasonsData.available_seasons);
        setRealCurrentSeason(availableSeasonsData.real_current_season);
      } catch (error) {
        safeConsole.error('Error initializing seasons', error);
        setError(error.message);
        
        // Fallback: try to use cached data or provide default
        const savedSeason = localStorage.getItem('selectedSeason');
        if (savedSeason && isValidSeasonFormat(savedSeason)) {
          setCurrentSeason(savedSeason);
        } else {
          // Provide a reasonable default as fallback
          const currentYear = new Date().getFullYear();
          const defaultSeason = `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
          setCurrentSeason(defaultSeason);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeSeasons();
  }, []);

  /**
   * Change the selected season
   * @param {string} newSeason - The season to switch to
   */
  const changeSeason = async (newSeason) => {
    if (!isValidSeasonFormat(newSeason)) {
      console.warn('Invalid season format:', newSeason);
      return;
    }

    // Check if season is in available seasons
    if (!availableSeasons.includes(newSeason)) {
      console.warn('Season not available:', newSeason);
      return;
    }

    setIsLoading(true);
    setCurrentSeason(newSeason);
    
    // Save to localStorage for persistence
    localStorage.setItem('selectedSeason', newSeason);
    
    // Simulate loading time for user feedback
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  /**
   * Reset to current season
   */
  const resetToCurrentSeason = async () => {
    try {
      const currentSeasonData = await SeasonService.getCurrentSeason();
      changeSeason(currentSeasonData.current_season);
    } catch (error) {
      console.error('❌ Error resetting to current season:', error);
      // Fallback to current selected season
    }
  };

  /**
   * Check if the selected season is the current/active season
   * @returns {boolean}
   */
  const isCurrentSeasonSelected = async () => {
    try {
      const currentSeasonData = await SeasonService.getCurrentSeason();
      return currentSeason === currentSeasonData.current_season;
    } catch (error) {
      console.error('❌ Error checking current season:', error);
      return false;
    }
  };

  /**
   * Get display name for current season
   * @returns {string}
   */
  const getCurrentSeasonDisplay = () => {
    if (!currentSeason) return '';
    const [startYear, endYearShort] = currentSeason.split('-');
    const endYear = `20${endYearShort}`;
    return `${startYear}/${endYear}`;
  };

  const contextValue = {
    // State
    currentSeason,
    realCurrentSeason,
    availableSeasons,
    isLoading,
    error,
    
    // Actions
    changeSeason,
    resetToCurrentSeason,
    
    // Utilities
    isCurrentSeasonSelected,
    getCurrentSeasonDisplay,
    isValidSeasonFormat,
    
    // Service access for advanced usage
    seasonService: SeasonService
  };

  return (
    <SeasonContext.Provider value={contextValue}>
      {children}
    </SeasonContext.Provider>
  );
};

/**
 * Hook to use the season context
 * @returns {object} Season context value
 */
export const useSeason = () => {
  const context = useContext(SeasonContext);
  
  if (!context) {
    throw new Error('useSeason must be used within a SeasonProvider');
  }
  
  return context;
};

/**
 * Hook to get current season for API calls
 * Provides current season with optional override
 * @param {string} overrideSeason - Optional season override
 * @returns {string} Season to use for API calls
 */
export const useSeasonForAPI = (overrideSeason = null) => {
  const { currentSeason, isValidSeasonFormat } = useSeason();
  
  return overrideSeason && isValidSeasonFormat(overrideSeason) 
    ? overrideSeason 
    : currentSeason;
};

/**
 * Higher-order component to provide season context
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with season context
 */
export const withSeason = (Component) => {
  return function WrappedComponent(props) {
    return (
      <SeasonProvider>
        <Component {...props} />
      </SeasonProvider>
    );
  };
};

export default SeasonContext;